// hooks/finance/usePaymentRequestActions.ts
import { supabase } from '@/integrations/supabase/client';
import { PaymentRequest } from '@/types/database/payment-requests';
import {
  notifyPaymentApproved,
  notifyPaymentRejected,
  notifyPaymentConfirmed,
} from '@/services/telegram/paymentNotificationService';

const sendDecisionEmail = async ({
  request,
  decision,
  reason,
  comments,
  actorName,
}: {
  request: {
    request_number: string;
    requester_first_name: string | null;
    requester_last_name: string | null;
    requester_email: string | null;
    title: string;
    total_amount: number;
    currency: string | null;
  };
  decision: 'approved' | 'rejected';
  reason?: string;
  comments?: string;
  actorName: string;
}) => {
  const requesterFullName =
    request.requester_first_name && request.requester_last_name
      ? `${request.requester_first_name} ${request.requester_last_name}`
      : null;

  if (!request.requester_email || !requesterFullName) return;

  try {
    await supabase.functions.invoke('send-payment-decision-email', {
      body: {
        request_number: request.request_number,
        requester_name: requesterFullName,
        requester_email: request.requester_email,
        title: request.title,
        total_amount: request.total_amount,
        currency: request.currency || 'ETB',
        decision,
        reason,
        comments,
        approver_name: actorName,
      },
    });
  } catch (e) {
    console.error(`Failed to send ${decision} email:`, e);
  }
};

export type ConfirmPaymentActionInput = {
  id: string;
  userId: string;
  paymentReference?: string;
  actualPaymentDate?: string;
  bankAccountId?: string;
  baseAmount?: number;
  vatRate?: number;
  vatAmount?: number;
  totalExpense?: number;
  withholdingRate?: number;
  withholdingAmount?: number;
  netPayment?: number;
};

const getActorName = async (userId?: string, fallback = 'Reviewer') => {
  if (!userId) return fallback;

  const { data } = await supabase
    .from('user_profiles')
    .select('first_name, last_name')
    .eq('id', userId)
    .single();

  return `${data?.first_name || ''} ${data?.last_name || ''}`.trim() || fallback;
};

export const approvePaymentRequestAction = async ({
  id,
  userId,
  comments,
  skipNotifications = false,
}: {
  id: string;
  userId: string;
  comments?: string;
  skipNotifications?: boolean;
}) => {
  const { data: request, error: fetchError } = await supabase
    .from('payment_requests')
    .select(
      'current_approval_level, department_id, total_amount, request_number, title, requester_first_name, requester_last_name, requester_email, currency'
    )
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  const { data: approverCheck, error: approverCheckError } = await supabase
    .from('department_approvers')
    .select('max_approval_amount')
    .eq('department_id', request.department_id)
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle();

  if (approverCheckError) throw approverCheckError;
  if (!approverCheck) throw new Error('You are not authorized to approve this request');

  if (
    approverCheck.max_approval_amount &&
    request.total_amount > approverCheck.max_approval_amount
  ) {
    throw new Error(
      `Request ${request.request_number} exceeds your approval limit (ETB ${approverCheck.max_approval_amount.toLocaleString()})`
    );
  }

  const { error: approvalError } = await supabase
    .from('payment_request_approvals')
    .insert({
      payment_request_id: id,
      approver_id: userId,
      approval_level: 1,
      status: 'approved',
      comments,
    });

  if (approvalError) throw approvalError;

  const { data: updatedRequest, error: updateError } = await supabase
    .from('payment_requests')
    .update({
      status: 'approved',
      approved_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (updateError) throw updateError;

  if (!skipNotifications) {
    const approverName = await getActorName(userId, 'Approver');
    await sendDecisionEmail({
      request,
      decision: 'approved',
      comments,
      actorName: approverName,
    });

    try {
      await notifyPaymentApproved(updatedRequest as PaymentRequest, approverName);
    } catch (e) {
      console.error('Failed to send Telegram notification:', e);
    }
  }

  return updatedRequest;
};

export const rejectPaymentRequestAction = async ({
  id,
  userId,
  reason,
  comments,
  skipNotifications = false,
}: {
  id: string;
  userId: string;
  reason: string;
  comments?: string;
  skipNotifications?: boolean;
}) => {
  const { data: request, error: fetchError } = await supabase
    .from('payment_requests')
    .select(
      'current_approval_level, department_id, request_number, title, total_amount, requester_first_name, requester_last_name, requester_email, currency, status'
    )
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  const { data: userProfile } = await supabase
    .from('user_profiles')
    .select('role')
    .eq('id', userId)
    .maybeSingle();

  const isFinanceUser = userProfile?.role === 'finance' || userProfile?.role === 'admin';

  if (request.status === 'approved') {
    if (!isFinanceUser) {
      throw new Error('Only finance users can reject approved requests');
    }
  } else if (request.status === 'pending_approval') {
    const { data: approverCheck, error: approverCheckError } = await supabase
      .from('department_approvers')
      .select('id')
      .eq('department_id', request.department_id)
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (approverCheckError) throw approverCheckError;
    if (!approverCheck) {
      throw new Error('You are not authorized to reject this request');
    }
  } else {
    throw new Error(`Request ${request.request_number} cannot be rejected`);
  }

  const { error: approvalError } = await supabase
    .from('payment_request_approvals')
    .insert({
      payment_request_id: id,
      approver_id: userId,
      approval_level: request.current_approval_level,
      status: 'rejected',
      comments: comments || reason,
    });

  if (approvalError) throw approvalError;

  const { data: updatedRequest, error: updateError } = await supabase
    .from('payment_requests')
    .update({
      status: 'rejected',
      rejection_reason: reason,
      rejected_by: userId,
      rejected_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (updateError) throw updateError;

  if (!skipNotifications) {
    const rejectorName = await getActorName(userId, 'Reviewer');
    await sendDecisionEmail({
      request,
      decision: 'rejected',
      reason,
      comments,
      actorName: rejectorName,
    });

    try {
      await notifyPaymentRejected(updatedRequest as PaymentRequest, reason, rejectorName);
    } catch (e) {
      console.error('Failed to send Telegram notification:', e);
    }
  }

  return updatedRequest;
};

export const confirmPaymentAction = async ({
  id,
  userId,
  paymentReference,
  actualPaymentDate,
  bankAccountId,
  baseAmount,
  vatRate,
  vatAmount,
  totalExpense,
  withholdingRate,
  withholdingAmount,
  netPayment,
  skipNotifications = false,
}: ConfirmPaymentActionInput & { skipNotifications?: boolean }) => {
  const paymentDate = actualPaymentDate || new Date().toISOString().split('T')[0];

  const { data: paymentRequest, error: fetchError } = await supabase
    .from('payment_requests')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  const { data: expenseNumber, error: numberError } = await supabase.rpc(
    'generate_expense_number'
  );

  if (numberError) {
    throw new Error(`Failed to generate expense number: ${numberError.message}`);
  }

  if (!expenseNumber) {
    throw new Error('Failed to generate expense number: No number returned');
  }

  const expenseDescription = paymentRequest.description
    ? `${paymentRequest.title} - ${paymentRequest.description}`
    : paymentRequest.title;

  const { error: expenseError } = await (supabase as any).from('expenses').insert({
    expense_number: expenseNumber,
    category_id: paymentRequest.category_id,
    department_id: paymentRequest.department_id,
    expense_date: paymentDate,
    description: expenseDescription,
    vendor_name: paymentRequest.vendor_name,
    reference_number: paymentRequest.request_number,
    amount: baseAmount ?? paymentRequest.amount,
    currency: paymentRequest.currency || 'ETB',
    vat_rate: vatRate ?? 0,
    vat_amount: vatAmount ?? paymentRequest.vat_amount ?? 0,
    total_amount: totalExpense ?? paymentRequest.total_amount,
    withholding_rate: withholdingRate ?? 0,
    withholding_amount: withholdingAmount ?? 0,
    net_payment: netPayment ?? paymentRequest.total_amount,
    payment_status: 'paid',
    payment_method: paymentRequest.payment_method,
    payment_date: paymentDate,
    payment_reference: paymentReference,
    payment_request_id: paymentRequest.id,
    business_unit: (paymentRequest as any).business_unit ?? 'chipchip',
    created_by: userId,
    approved_by: userId,
    approved_at: new Date().toISOString(),
    bank_account_id: bankAccountId || null,
  });

  if (expenseError) {
    throw new Error(`Failed to create expense record: ${expenseError.message}`);
  }

  // Fetch the newly created expense to get its id, then copy BU allocations
  try {
    const { data: createdExpense } = await (supabase as any)
      .from('expenses')
      .select('id')
      .eq('expense_number', expenseNumber)
      .maybeSingle();

    if (createdExpense?.id) {
      const { data: prAllocs } = await (supabase as any)
        .from('payment_request_bu_allocations')
        .select('business_unit, percentage')
        .eq('payment_request_id', paymentRequest.id);

      if (prAllocs && prAllocs.length > 0) {
        await (supabase as any).rpc('set_expense_bu_allocations', {
          p_expense_id: createdExpense.id,
          p_allocations: prAllocs.map((a: any) => ({
            business_unit: a.business_unit,
            percentage: Number(a.percentage),
          })),
        });
      }
    }
  } catch (e) {
    console.error('Failed to copy BU allocations to expense:', e);
  }

  const { data: updatedRequest, error: updateError } = await supabase
    .from('payment_requests')
    .update({
      status: 'paid',
      paid_at: new Date().toISOString(),
      paid_by: userId,
      payment_reference: paymentReference,
      actual_payment_date: paymentDate,
    })
    .eq('id', id)
    .select()
    .single();

  if (updateError) {
    await supabase.from('expenses').delete().eq('expense_number', expenseNumber);
    throw new Error(`Failed to update payment status: ${updateError.message}`);
  }

  if (!skipNotifications) {
    try {
      await notifyPaymentConfirmed(updatedRequest as PaymentRequest, paymentReference);
    } catch (e) {
      console.error('Failed to send Telegram notification:', e);
    }
  }

  return updatedRequest;
};